import { useState } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useAuth } from "@/context/AuthContext";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const DOT_GRID =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='36' height='36'%3E%3Ccircle cx='18' cy='18' r='1' fill='rgba(90,144,168,0.10)'/%3E%3C/svg%3E\")";

/* ── Icons ────────────────────────────────────────────────────── */
const GoogleLogo = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const BackArrow = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M19 12H5M12 5l-7 7 7 7" />
  </svg>
);

const EyeOpen = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeClosed = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

/* ── Shared styles ────────────────────────────────────────────── */
const fieldBase = (focused) => ({
  display: "block",
  width: "100%",
  padding: "12px 14px",
  border: `1px solid ${focused ? "#5A90A8" : "rgba(90,144,168,0.25)"}`,
  borderRadius: 10,
  fontSize: 14,
  fontFamily: "Inter, sans-serif",
  color: "#1C3545",
  background: "#F5FAFC",
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color 180ms ease, box-shadow 180ms ease",
  boxShadow: focused ? "0 0 0 3px rgba(90,144,168,0.12)" : "none",
});

const labelRow  = { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 };
const labelText = { fontSize: 13, fontWeight: 500, color: "#4D7A8E", fontFamily: "Inter, sans-serif" };
const requiredDot = { color: "#5A90A8", marginLeft: 2 };

/* ── Role badge ───────────────────────────────────────────────── */
function RoleBadge({ role }) {
  const isDoctor = role === "doctor";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "Inter, sans-serif", background: isDoctor ? "rgba(90,144,168,0.1)" : "rgba(29,78,216,0.1)", color: isDoctor ? "#5A90A8" : "#1D4ED8", border: `1px solid ${isDoctor ? "rgba(90,144,168,0.3)" : "rgba(29,78,216,0.3)"}` }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: isDoctor ? "#5A90A8" : "#1D4ED8" }} />
      {isDoctor ? "Doctor Account" : "Patient Account"}
    </span>
  );
}

/* ── Google signup sub-component ── */
function GoogleSignupButton({ onProfileReceived, onError, onLoadingChange, disabled, loading }) {
  const googleSignup = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const res = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch Google profile");
        const profile = await res.json();
        onProfileReceived({ sub: profile.sub, email: profile.email, name: profile.name });
      } catch {
        onError("Failed to get Google profile. Please try again.");
      }
    },
    onError: () => onError("Google sign-up was cancelled or failed."),
  });

  return (
    <button
      type="button"
      onClick={() => { onLoadingChange(true); googleSignup(); }}
      disabled={disabled}
      style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, width: "100%", padding: "12px 16px", background: "#FFFFFF", color: "#1f1f1f", border: "1px solid rgba(90,144,168,0.25)", borderRadius: 10, fontSize: 14, fontWeight: 600, fontFamily: "Inter, sans-serif", cursor: loading ? "not-allowed" : "pointer", marginBottom: 24, transition: "background 180ms ease, transform 180ms ease, box-shadow 180ms ease" }}
      onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.background = "#F5FAFC"; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(90,144,168,0.15)"; } }}
      onMouseLeave={(e) => { if (!loading) { e.currentTarget.style.background = "#FFFFFF"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; } }}
    >
      <GoogleLogo />
      {loading ? "Please wait…" : "Sign up with Google"}
    </button>
  );
}

/* ════════════════════════════════════════════════════════════════
   Main component
   ════════════════════════════════════════════════════════════════ */
export default function CreateAccountPage({ onBack, onLoginClick, onDoctorSignupSuccess, onPatientSignupSuccess, initialRole = "patient" }) {
  const auth = useAuth();

  const [stage, setStage] = useState("entry");
  const [role, setRole]   = useState(initialRole);

  const [entryEmail, setEntryEmail]     = useState("");
  const [entryFocused, setEntryFocused] = useState(false);

  const [isGoogleFlow, setIsGoogleFlow]     = useState(false);
  const [googleUserData, setGoogleUserData] = useState(null);

  const [name, setName]                     = useState("");
  const [nameFocused, setNameFocused]       = useState(false);
  const [profileEmail, setProfileEmail]     = useState("");
  const [profileEmailFocused, setPEFocused] = useState(false);
  const [password, setPassword]             = useState("");
  const [pwFocused, setPwFocused]           = useState(false);
  const [showPw, setShowPw]                 = useState(false);
  const [isPractitioner, setIsPractitioner] = useState(initialRole === "doctor");

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const handleEmailContinue = (e) => {
    e.preventDefault();
    if (!entryEmail.trim()) return;
    setProfileEmail(entryEmail.trim());
    setIsGoogleFlow(false);
    setStage("profile");
  };

  const handleProfileBack = () => {
    setStage("entry");
    setName(""); setPassword(""); setIsPractitioner(initialRole === "doctor");
    setIsGoogleFlow(false); setGoogleUserData(null);
    setError("");
  };

  const handlePractitionerToggle = () => {
    const next = !isPractitioner;
    setIsPractitioner(next);
    setRole(next ? "doctor" : "patient");
  };

  const handleGoogleProfileReceived = (profile) => {
    setError("");
    setGoogleUserData(profile);
    setProfileEmail(profile.email || "");
    setName(profile.name || "");
    setIsGoogleFlow(true);
    setStage("profile");
    setLoading(false);
  };

  const handleGoogleSignupError = (msg) => {
    setError(msg);
    setLoading(false);
  };

  const canSubmit =
    !loading &&
    name.trim() !== "" &&
    profileEmail.trim() !== "" &&
    (isGoogleFlow || password.length >= 8);

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");
    setLoading(true);
    try {
      if (isGoogleFlow && googleUserData) {
        await auth.socialLogin({
          provider: "google",
          provider_id: googleUserData.sub,
          email: googleUserData.email,
          name: name.trim(),
          role,
        });
      } else {
        await auth.signup({
          name: name.trim(),
          email: profileEmail.trim(),
          password,
          role,
        });
      }
      if (role === "doctor" && onDoctorSignupSuccess) {
        onDoctorSignupSuccess(name.trim());
      } else if (role === "patient" && onPatientSignupSuccess) {
        onPatientSignupSuccess(name.trim());
      } else {
        onLoginClick();
      }
    } catch (err) {
      console.error("Signup error:", err);
      let msg;
      if (!err.response) {
        msg = `Cannot connect to server. Make sure the backend is running on ${API_BASE}`;
      } else {
        const detail = err.response?.data?.detail;
        if (typeof detail === "string") {
          msg = detail;
        } else if (Array.isArray(detail)) {
          msg = detail.map((d) => d.msg).join(", ");
        } else {
          msg = `Server error ${err.response.status}: ${err.response.statusText || "Account creation failed."}`;
        }
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const pageStyle = { position: "relative", minHeight: "100vh", background: "#EDF4F7", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 24px", paddingTop: "140px", paddingBottom: "60px", overflow: "hidden" };
  const Bg = <div aria-hidden style={{ position: "absolute", inset: 0, backgroundImage: DOT_GRID, backgroundRepeat: "repeat", pointerEvents: "none", zIndex: 1 }} />;
  const cardStyle = { position: "relative", zIndex: 2, background: "#FFFFFF", border: "1px solid rgba(90,144,168,0.18)", borderRadius: 22, padding: "40px 36px 36px", width: "100%", maxWidth: 440, boxShadow: "0 4px 32px rgba(90,144,168,0.12)" };

  /* ════════════════════════════════════════════════════════════ */
  /* STAGE: ENTRY                                                 */
  /* ════════════════════════════════════════════════════════════ */
  if (stage === "entry") {
    return (
      <div style={pageStyle}>
        {Bg}
        <div style={cardStyle}>
          {/* Wordmark */}
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <button type="button" onClick={onBack} className="font-serif-display" style={{ fontSize: 19, color: "#1C3545", background: "none", border: "none", cursor: "pointer", letterSpacing: "-0.035em", padding: 0 }}>
              Supercore Longevity
            </button>
            <div aria-hidden style={{ width: 28, height: 2, background: "#8AAEBB", borderRadius: 99, margin: "10px auto 0", opacity: 0.8 }} />
          </div>

          {/* Role badge + heading */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
            <h1 style={{ margin: 0, fontSize: 21, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em" }}>
              Create account
            </h1>
            <RoleBadge role={role} />
          </div>
          <p style={{ margin: "0 0 26px", fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif", lineHeight: 1.5 }}>
            Already have one?{" "}
            <button type="button" onClick={onLoginClick} style={{ background: "none", border: "none", cursor: "pointer", color: "#5A90A8", fontWeight: 600, fontSize: 13, fontFamily: "Inter, sans-serif", padding: 0 }}
              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}>
              Sign in
            </button>
          </p>

          {error && (
            <div style={{ marginBottom: 16, padding: "11px 14px", borderRadius: 9, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)", color: "#DC2626", fontSize: 13, fontFamily: "Inter, sans-serif", lineHeight: 1.45 }}>
              {error}
            </div>
          )}

          <form onSubmit={handleEmailContinue}>
            <div style={{ marginBottom: 16 }}>
              <div style={labelRow}>
                <span style={labelText}>Email address<span style={requiredDot}>*</span></span>
              </div>
              <input
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={entryEmail}
                onChange={(e) => setEntryEmail(e.target.value)}
                onFocus={() => setEntryFocused(true)}
                onBlur={() => setEntryFocused(false)}
                style={fieldBase(entryFocused)}
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              disabled={!entryEmail.trim() || loading}
              style={{
                display: "block", width: "100%", padding: "13px",
                background: entryEmail.trim() && !loading ? "#5A90A8" : "rgba(90,144,168,0.25)",
                color: entryEmail.trim() && !loading ? "#FFFFFF" : "rgba(90,144,168,0.5)",
                border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700,
                fontFamily: "Inter, sans-serif", cursor: entryEmail.trim() && !loading ? "pointer" : "not-allowed",
                letterSpacing: "0.01em", transition: "background 180ms ease, transform 180ms ease, box-shadow 180ms ease",
              }}
              onMouseEnter={(e) => { if (entryEmail.trim() && !loading) { e.currentTarget.style.background = "#4D7A8E"; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(90,144,168,0.3)"; } }}
              onMouseLeave={(e) => { if (entryEmail.trim() && !loading) { e.currentTarget.style.background = "#5A90A8"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; } }}
            >
              Continue →
            </button>
          </form>

          {GOOGLE_CLIENT_ID && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "22px 0" }}>
                <div style={{ flex: 1, height: 1, background: "rgba(90,144,168,0.15)" }} />
                <span style={{ fontSize: 12, color: "#9BBCCC", fontFamily: "Inter, sans-serif" }}>or</span>
                <div style={{ flex: 1, height: 1, background: "rgba(90,144,168,0.15)" }} />
              </div>
              <GoogleSignupButton
                onProfileReceived={handleGoogleProfileReceived}
                onError={handleGoogleSignupError}
                onLoadingChange={setLoading}
                disabled={loading}
                loading={loading}
              />
            </>
          )}

          {/* Role switcher */}
          <div style={{ borderTop: "1px solid rgba(90,144,168,0.12)", paddingTop: 18, textAlign: "center" }}>
            {role === "patient" ? (
              <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
                Registering as a doctor?{" "}
                <button type="button" onClick={() => setRole("doctor")}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#5A90A8", fontWeight: 600, fontSize: 13, fontFamily: "Inter, sans-serif", padding: 0 }}
                  onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                  onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}>
                  Create doctor account →
                </button>
              </p>
            ) : (
              <p style={{ margin: 0, fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
                Registering as a patient?{" "}
                <button type="button" onClick={() => setRole("patient")}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#1D4ED8", fontWeight: 600, fontSize: 13, fontFamily: "Inter, sans-serif", padding: 0 }}
                  onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                  onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}>
                  Create patient account →
                </button>
              </p>
            )}
          </div>

          <p style={{ textAlign: "center", fontSize: 11, color: "#A8C8D8", marginTop: 18, marginBottom: 0, lineHeight: 1.6, fontFamily: "Inter, sans-serif" }}>
            By continuing you agree to our{" "}
            <a href="#terms" style={{ color: "#7AAABB", textDecoration: "underline" }}>Terms</a>
            {" "}and{" "}
            <a href="#privacy" style={{ color: "#7AAABB", textDecoration: "underline" }}>Privacy Policy</a>.
          </p>
        </div>

        <button type="button" onClick={onBack} style={{ position: "relative", zIndex: 2, marginTop: 22, background: "none", border: "none", cursor: "pointer", color: "#9BBCCC", fontSize: 13, fontFamily: "Inter, sans-serif", display: "flex", alignItems: "center", gap: 6, transition: "color 150ms ease" }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#1C3545")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#9BBCCC")}>
          ← Back to login
        </button>
      </div>
    );
  }

  /* ════════════════════════════════════════════════════════════ */
  /* STAGE: PROFILE                                               */
  /* ════════════════════════════════════════════════════════════ */
  return (
    <div style={{ ...pageStyle, maxWidth: "100%" }}>
      {Bg}

      <div style={{ ...cardStyle, maxWidth: 480 }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 24 }}>
          <div>
            <button
              type="button"
              onClick={handleProfileBack}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(90,144,168,0.06)", border: "1px solid rgba(90,144,168,0.2)", borderRadius: 8, padding: "6px 12px", color: "#7AAABB", fontSize: 12, fontFamily: "Inter, sans-serif", cursor: "pointer", marginBottom: 14, transition: "border-color 150ms, color 150ms" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#5A90A8"; e.currentTarget.style.color = "#5A90A8"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(90,144,168,0.2)"; e.currentTarget.style.color = "#7AAABB"; }}
            >
              <BackArrow /> Back
            </button>

            {isGoogleFlow && (
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10, padding: "7px 12px", borderRadius: 8, background: "rgba(66,133,244,0.08)", border: "1px solid rgba(66,133,244,0.2)" }}>
                <GoogleLogo />
                <span style={{ fontSize: 12, color: "#4D7A8E", fontFamily: "Inter, sans-serif" }}>Signed in with Google</span>
              </div>
            )}

            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#1C3545", fontFamily: "Inter, sans-serif", letterSpacing: "-0.02em", lineHeight: 1.2 }}>
              Complete your profile
            </h1>
            <p style={{ margin: "5px 0 0", fontSize: 13, color: "#7AAABB", fontFamily: "Inter, sans-serif" }}>
              All fields marked <span style={{ color: "#5A90A8" }}>*</span> are required.
            </p>
          </div>
          <RoleBadge role={role} />
        </div>

        {error && (
          <div style={{ marginBottom: 18, padding: "11px 14px", borderRadius: 9, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)", color: "#DC2626", fontSize: 13, fontFamily: "Inter, sans-serif", lineHeight: 1.45 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleCreateAccount}>

          {/* Full name */}
          <div style={{ marginBottom: 18 }}>
            <div style={labelRow}>
              <span style={labelText}>Full name<span style={requiredDot}>*</span></span>
            </div>
            <input
              type="text"
              autoComplete="name"
              placeholder="Your full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onFocus={() => setNameFocused(true)}
              onBlur={() => setNameFocused(false)}
              style={fieldBase(nameFocused)}
              disabled={loading}
            />
          </div>

          {/* Email */}
          <div style={{ marginBottom: 18 }}>
            <div style={labelRow}>
              <span style={labelText}>Email address<span style={requiredDot}>*</span></span>
            </div>
            <input
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={profileEmail}
              onChange={(e) => setProfileEmail(e.target.value)}
              onFocus={() => setPEFocused(true)}
              onBlur={() => setPEFocused(false)}
              style={{ ...fieldBase(profileEmailFocused), opacity: isGoogleFlow ? 0.7 : 1 }}
              readOnly={isGoogleFlow}
              disabled={loading}
            />
          </div>

          {/* Password (email flow only) */}
          {!isGoogleFlow && (
            <div style={{ marginBottom: 18 }}>
              <div style={labelRow}>
                <span style={labelText}>Password<span style={requiredDot}>*</span></span>
                <span style={{ fontSize: 11, color: "#A8C8D8", fontFamily: "Inter, sans-serif" }}>min 8 chars, 1 digit</span>
              </div>
              <div style={{ position: "relative" }}>
                <input
                  type={showPw ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={() => setPwFocused(true)}
                  onBlur={() => setPwFocused(false)}
                  style={{ ...fieldBase(pwFocused), paddingRight: 40 }}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: showPw ? "#5A90A8" : "#9BBCCC", padding: 0, display: "flex", alignItems: "center", transition: "color 150ms ease" }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#5A90A8")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = showPw ? "#5A90A8" : "#9BBCCC")}
                >
                  {showPw ? <EyeClosed /> : <EyeOpen />}
                </button>
              </div>
            </div>
          )}

          {/* Practitioner checkbox */}
          {role === "doctor" && <div
            style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "14px 16px", borderRadius: 12, border: `1px solid ${isPractitioner ? "rgba(90,144,168,0.5)" : "rgba(90,144,168,0.18)"}`, background: isPractitioner ? "rgba(90,144,168,0.07)" : "rgba(90,144,168,0.02)", cursor: "pointer", marginBottom: 26, transition: "border-color 180ms, background 180ms" }}
            onClick={handlePractitionerToggle}
            role="checkbox"
            aria-checked={isPractitioner}
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") { e.preventDefault(); handlePractitionerToggle(); } }}
          >
            <div style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0, marginTop: 1, border: `2px solid ${isPractitioner ? "#5A90A8" : "rgba(90,144,168,0.35)"}`, background: isPractitioner ? "#5A90A8" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 150ms ease" }}>
              {isPractitioner && (
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden>
                  <polyline points="2,6 5,9 10,3" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: isPractitioner ? "#5A90A8" : "#4D7A8E", fontFamily: "Inter, sans-serif", lineHeight: 1.3, transition: "color 150ms" }}>
                I am a licensed practitioner
              </p>
              <p style={{ margin: "4px 0 0", fontSize: 12, color: "#7AAABB", fontFamily: "Inter, sans-serif", lineHeight: 1.5 }}>
                I confirm I hold a valid medical or clinical license to practise. This will register you as a Doctor.
              </p>
            </div>
          </div>}

          {/* Submit */}
          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              display: "block", width: "100%", padding: "13px",
              background: canSubmit ? "#5A90A8" : "rgba(90,144,168,0.25)",
              color: canSubmit ? "#FFFFFF" : "rgba(90,144,168,0.5)",
              border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700,
              fontFamily: "Inter, sans-serif", cursor: canSubmit ? "pointer" : "not-allowed",
              letterSpacing: "0.01em", transition: "background 180ms ease, transform 180ms ease, box-shadow 180ms ease",
            }}
            onMouseEnter={(e) => { if (canSubmit) { e.currentTarget.style.background = "#4D7A8E"; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(90,144,168,0.3)"; } }}
            onMouseLeave={(e) => { if (canSubmit) { e.currentTarget.style.background = "#5A90A8"; e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; } }}
          >
            {loading ? "Creating account…" : `Create ${role === "doctor" ? "Doctor" : "Patient"} Account`}
          </button>

          <p style={{ textAlign: "center", fontSize: 11, color: "#A8C8D8", marginTop: 18, marginBottom: 0, lineHeight: 1.6, fontFamily: "Inter, sans-serif" }}>
            By creating an account you agree to our{" "}
            <a href="#terms" style={{ color: "#7AAABB", textDecoration: "underline" }}>Terms</a>
            {" "}and{" "}
            <a href="#privacy" style={{ color: "#7AAABB", textDecoration: "underline" }}>Privacy Policy</a>.
          </p>
        </form>
      </div>
    </div>
  );
}
